import React, { useEffect, useState, useRef } from 'react';
import { Form, Input, Select, Button, Upload, message, List, Space, Alert } from 'antd';
import { UploadOutlined, PaperClipOutlined, DeleteOutlined, LoadingOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import RichTextEditor from '../../components/RichTextEditor';

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const NewsForm = () => {
    const quillRef = useRef();
    const [form] = Form.useForm();
    const [categories, setCategories] = useState([]);
    const { id } = useParams();
    const navigate = useNavigate();

    // ไฟล์แนบข่าว
    const [attachments, setAttachments] = useState([]);      // ไฟล์ที่อัปโหลดแล้ว (จากระบบ)
    const [pendingFiles, setPendingFiles] = useState([]);    // ไฟล์ที่เลือกใหม่ ยังไม่ถูกอัปโหลด
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);

    // สถานะสิทธิ์ผู้ใช้ + สถานะข่าว (สำหรับระบบอนุมัติ)
    const [profile, setProfile] = useState(null);
    const [newsStatus, setNewsStatus] = useState(null);

    useEffect(() => {
        axios.get(`${API_BASE_URL}/api/news-categories`).then((res) => setCategories(res.data));

        // โหลดโปรไฟล์ผู้ใช้ (ตรวจระดับ)
        try {
            const user = JSON.parse(localStorage.getItem('user'));
            setProfile(user);
        } catch {
            setProfile(null);
        }

        if (id) {
            axios.get(`${API_BASE_URL}/api/news/${id}`).then((res) => {
                form.setFieldsValue(res.data);
                setNewsStatus(res.data.status || null);
            });
            axios.get(`${API_BASE_URL}/api/news-attachments/${id}`).then((res) => {
                setAttachments(res.data?.attachments || []);
            }).catch(() => setAttachments([]));
        }
    }, [id, form]);

    const isAdmin = profile?.level === 'ภาค';

    const extractFirstImage = (htmlContent) => {
        const doc = new DOMParser().parseFromString(htmlContent, 'text/html');
        const img = doc.querySelector('img');
        return img ? img.src : null;
    };

    // เก็บไฟล์ที่เลือกไว้ก่อน (ยังไม่อัปโหลด) — จะอัปโหลดตอนบันทึกข่าว
    const handleBeforeUpload = (file) => {
        setPendingFiles((prev) => [...prev, file]);
        return false;
    };

    const removePendingFile = (index) => {
        setPendingFiles((prev) => prev.filter((_, i) => i !== index));
    };

    // อัปโหลดไฟล์ที่ค้างไว้ทั้งหมด
    const uploadPendingFiles = async (newsId) => {
        for (const file of pendingFiles) {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('newsId', newsId);
            await axios.post(`${API_BASE_URL}/api/news-attachments`, fd);
        }
    };

    // ลบไฟล์แนบที่อัปโหลดแล้ว
    const handleDeleteAttachment = async (att) => {
        try {
            await axios.delete(`${API_BASE_URL}/api/news-attachments/${att.id}`);
            setAttachments((prev) => prev.filter((a) => a.id !== att.id));
            message.success('ลบไฟล์แนบเรียบร้อย');
        } catch {
            message.error('ไม่สามารถลบไฟล์แนบได้');
        }
    };

    const onFinish = async (values) => {
        const user = JSON.parse(localStorage.getItem('user'));
        values.author_id = user.id
        values.author = user.name

        // ดึงรูปแรกมาเก็บไว้ในฟิลด์ cover_image
        const firstImage = extractFirstImage(values.content);
        if (firstImage) {
            values.cover_image = firstImage;
        } else {
            values.cover_image = '';
        }

        setSaving(true);
        try {
            let newsId = id;
            if (id) {
                await axios.put(`${API_BASE_URL}/api/news/${id}`, values);
                newsId = id;
            } else {
                const res = await axios.post(`${API_BASE_URL}/api/news`, values);
                newsId = res.data?.id;
            }

            // อัปโหลดไฟล์ที่เลือกค้างไว้ (ถ้ามี)
            if (pendingFiles.length > 0 && newsId) {
                setUploading(true);
                await uploadPendingFiles(newsId);
                setPendingFiles([]);
                setUploading(false);
            }

            message.success(
              isAdmin
                ? id ? 'อัปเดตข่าวเรียบร้อย' : 'สร้างข่าวเรียบร้อย'
                : 'บันทึกข่าวเรียบร้อย — กำลังรอการอนุมัติจากผู้ดูแลระดับภาค'
            );
            navigate('/news-managments');
        } catch {
            message.error('ไม่สามารถบันทึกข่าวได้');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Form form={form} layout="vertical" onFinish={onFinish}>
            {!isAdmin && !id && (
                <Alert
                    type="info"
                    showIcon
                    message="ข่าวของคุณจะถูกนำเสนอต่อสาธารณะ หลังได้รับการอนุมัติจากผู้ดูแลระดับภาค"
                    style={{ marginBottom: 16 }}
                />
            )}
            {!isAdmin && id && newsStatus === 'rejected' && (
                <Alert
                    type="warning"
                    showIcon
                    message="ข่าวนี้ถูกปฏิเสธการเผยแพร่ — เมื่อบันทึกใหม่จะกลับเป็นสถานะรออนุมัติอีกครั้ง"
                    style={{ marginBottom: 16 }}
                />
            )}
            <Form.Item>
                <Button type="primary" htmlType="submit" style={{ float: 'right' }} loading={saving}>
                    {id ? 'อัปเดตข่าว' : 'สร้างข่าว'}
                </Button>
            </Form.Item>
            <Form.Item name="title" label="หัวข้อข่าว" rules={[{ required: true }]}>
                <Input />
            </Form.Item>
            <Form.Item name="category_id" label="หมวดหมู่" rules={[{ required: true }]}>
                <Select options={categories.map((c) => ({ value: c.id, label: c.name }))} />
            </Form.Item>

            <Form.Item name="content" label="เนื้อหาข่าว" rules={[{ required: true }]} style={{ padding: '10px' }}>
                <RichTextEditor
                    value={form.getFieldValue('content')}
                    onChange={(value) => form.setFieldsValue({ content: value })}
                />
            </Form.Item>

            {/* ไฟล์เอกสารแนบ */}
            <Form.Item label="ไฟล์เอกสารแนบ" style={{ padding: '10px' }}>
                <Upload
                    multiple
                    beforeUpload={handleBeforeUpload}
                    showUploadList={false}
                    disabled={uploading}
                >
                    <Button icon={uploading ? <LoadingOutlined /> : <UploadOutlined />} disabled={uploading}>
                        เลือกไฟล์เอกสาร (PDF, รูป, DOCX, XLSX, PPTX, TXT)
                    </Button>
                </Upload>
                {!id && pendingFiles.length > 0 && (
                    <div style={{ marginTop: 8, color: '#8a6d3b', fontSize: 13 }}>
                        ไฟล์ที่เลือกจะถูกอัปโหลดหลังบันทึกข่าวเรียบร้อยแล้ว
                    </div>
                )}

                {/* ไฟล์ที่อัปโหลดแล้ว */}
                {attachments.length > 0 && (
                    <List
                        size="small"
                        style={{ marginTop: 8 }}
                        header={<strong>ไฟล์ที่อัปโหลดแล้ว ({attachments.length})</strong>}
                        dataSource={attachments}
                        renderItem={(att) => (
                            <List.Item
                                actions={[
                                    <Button
                                        key="del"
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => handleDeleteAttachment(att)}
                                    >
                                        ลบ
                                    </Button>,
                                ]}
                            >
                                <Space>
                                    <PaperClipOutlined />
                                    <span>{att.original_name}</span>
                                    <span style={{ color: '#999' }}>({formatFileSize(att.file_size)})</span>
                                </Space>
                            </List.Item>
                        )}
                    />
                )}

                {/* ไฟล์ที่เลือกค้างไว้ */}
                {pendingFiles.length > 0 && (
                    <List
                        size="small"
                        style={{ marginTop: 8 }}
                        header={<strong>ไฟล์ที่เลือก ({pendingFiles.length}) — รออัปโหลด</strong>}
                        dataSource={pendingFiles}
                        renderItem={(file, index) => (
                            <List.Item
                                actions={[
                                    <Button
                                        key="del"
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => removePendingFile(index)}
                                    >
                                        เอาออก
                                    </Button>,
                                ]}
                            >
                                <Space>
                                    <PaperClipOutlined />
                                    <span>{file.name}</span>
                                    <span style={{ color: '#999' }}>({formatFileSize(file.size)})</span>
                                </Space>
                            </List.Item>
                        )}
                    />
                )}
            </Form.Item>
        </Form>
    );
};

export default NewsForm;